import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Title1, Subtitle2, Field, Input, Button, Spinner, MessageBar, TabList, Tab } from '@fluentui/react-components'
import { apiFetchJson } from '../lib/apiFetch'
import { useMe } from '../lib/me'
import { DEFAULT_SYNC_FRESH_DAYS, DEFAULT_SYNC_STALE_DAYS } from '../lib/syncFreshness'
import AccessTab from '../components/AccessTab'
import type { AppSettings, SyncTriggerResponse } from '../types'

const DEFAULT_LOG_RETENTION_DAYS = 365

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams()
  const me = useMe()
  const activeTab = searchParams.get('tab') === 'access' ? 'access' : 'general'
  const [teamsWebhookUrl, setTeamsWebhookUrl] = useState('')
  const [syncFreshDays, setSyncFreshDays] = useState(String(DEFAULT_SYNC_FRESH_DAYS))
  const [syncStaleDays, setSyncStaleDays] = useState(String(DEFAULT_SYNC_STALE_DAYS))
  const [logRetentionDays, setLogRetentionDays] = useState(String(DEFAULT_LOG_RETENTION_DAYS))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [syncingDirectory, setSyncingDirectory] = useState(false)
  const [directorySyncMessage, setDirectorySyncMessage] = useState<string | null>(null)
  const [directorySyncError, setDirectorySyncError] = useState<string | null>(null)
  const [directorySyncWarnings, setDirectorySyncWarnings] = useState<string | null>(null)

  useEffect(() => {
    apiFetchJson<AppSettings>('/api/settings')
      .then(data => {
        setTeamsWebhookUrl(data.teamsWebhookUrl ?? '')
        setSyncFreshDays(String(data.syncFreshDays ?? DEFAULT_SYNC_FRESH_DAYS))
        setSyncStaleDays(String(data.syncStaleDays ?? DEFAULT_SYNC_STALE_DAYS))
        setLogRetentionDays(String(data.logRetentionDays ?? DEFAULT_LOG_RETENTION_DAYS))
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false))
  }, [])

  async function handleSave() {
    setSaving(true)
    setSaved(false)
    setError(null)
    try {
      await apiFetchJson('/api/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamsWebhookUrl,
          syncFreshDays: Number(syncFreshDays) || DEFAULT_SYNC_FRESH_DAYS,
          syncStaleDays: Number(syncStaleDays) || DEFAULT_SYNC_STALE_DAYS,
          logRetentionDays: Number(logRetentionDays) || DEFAULT_LOG_RETENTION_DAYS,
        }),
      })
      setSaved(true)
    } catch (e) {
      setError(String(e))
    } finally {
      setSaving(false)
    }
  }

  async function handleSyncDirectory() {
    setSyncingDirectory(true)
    setDirectorySyncMessage(null)
    setDirectorySyncError(null)
    setDirectorySyncWarnings(null)
    try {
      const res = await apiFetchJson<SyncTriggerResponse>('/api/sync/directory', { method: 'POST' })
      const { recordsProcessed, errors } = res.result
      setDirectorySyncMessage(
        `Synced ${recordsProcessed} ${recordsProcessed === 1 ? 'person' : 'people'} from Entra ID` +
          (errors.length ? `, ${errors.length} error${errors.length === 1 ? '' : 's'}` : '.'),
      )
      if (errors.length) setDirectorySyncWarnings(errors.join(' '))
    } catch (e) {
      setDirectorySyncError(String(e))
    } finally {
      setSyncingDirectory(false)
    }
  }

  if (!me.isAdmin) {
    return (
      <div>
        <Title1>Settings</Title1>
        <MessageBar intent="warning" style={{ marginTop: 16 }}>
          Admin access required.
        </MessageBar>
      </div>
    )
  }

  return (
    <div>
      <Title1>Settings</Title1>

      <TabList
        selectedValue={activeTab}
        onTabSelect={(_e, data) =>
          setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            next.set('tab', data.value as string)
            return next
          })
        }
        style={{ marginTop: 12 }}
      >
        <Tab value="general">General</Tab>
        <Tab value="access">Access</Tab>
      </TabList>

      {activeTab === 'access' ? (
        <div style={{ marginTop: 24 }}>
          <AccessTab />
        </div>
      ) : loading ? (
        <Spinner label="Loading settings..." style={{ marginTop: 24 }} />
      ) : (
        <div style={{ marginTop: 24, maxWidth: 480 }}>
          <Field label="Teams Incoming Webhook URL" hint="Used to post orphaned-license alerts to the IT channel">
            <Input
              value={teamsWebhookUrl}
              onChange={(_e, data) => setTeamsWebhookUrl(data.value)}
              placeholder="https://...webhook.office.com/..."
            />
          </Field>

          <Subtitle2 style={{ display: 'block', marginTop: 24, marginBottom: 4 }}>
            Dashboard sync-freshness colors
          </Subtitle2>
          <p style={{ marginTop: 0, marginBottom: 16 }}>
            How old a service's last sync can be before the Dashboard flags it yellow, then red.
          </p>
          <div style={{ display: 'flex', gap: 16 }}>
            <Field label="Green up to (days)">
              <Input
                type="number"
                value={syncFreshDays}
                onChange={(_e, data) => setSyncFreshDays(data.value)}
                style={{ maxWidth: 100 }}
              />
            </Field>
            <Field label="Yellow up to (days)">
              <Input
                type="number"
                value={syncStaleDays}
                onChange={(_e, data) => setSyncStaleDays(data.value)}
                style={{ maxWidth: 100 }}
              />
            </Field>
          </div>

          <Subtitle2 style={{ display: 'block', marginTop: 24, marginBottom: 4 }}>Log retention</Subtitle2>
          <p style={{ marginTop: 0, marginBottom: 16 }}>
            How long activity and sync logs (see the Logs page) are kept before a daily job purges
            them.
          </p>
          <Field label="Keep for (days)">
            <Input
              type="number"
              value={logRetentionDays}
              onChange={(_e, data) => setLogRetentionDays(data.value)}
              style={{ maxWidth: 100 }}
            />
          </Field>

          <Button appearance="primary" onClick={handleSave} disabled={saving} style={{ marginTop: 16 }}>
            {saving ? 'Saving...' : 'Save'}
          </Button>

          {saved && <MessageBar intent="success" style={{ marginTop: 16 }}>Settings saved.</MessageBar>}
          {error && <MessageBar intent="error" style={{ marginTop: 16 }}>Error: {error}</MessageBar>}

          <Subtitle2 style={{ display: 'block', marginTop: 32, marginBottom: 4 }}>
            User directory
          </Subtitle2>
          <p style={{ marginTop: 0, marginBottom: 16 }}>
            Refreshes the Persons directory from Entra ID (also runs automatically every hour) —
            see the Users page in the sidebar for the full synced list.
          </p>
          <Button appearance="secondary" onClick={handleSyncDirectory} disabled={syncingDirectory}>
            {syncingDirectory ? 'Syncing...' : 'Sync directory now'}
          </Button>
          {directorySyncMessage && (
            <MessageBar intent="success" style={{ marginTop: 16 }}>
              {directorySyncMessage}
            </MessageBar>
          )}
          {directorySyncError && (
            <MessageBar intent="error" style={{ marginTop: 16 }}>
              Error: {directorySyncError}
            </MessageBar>
          )}
          {directorySyncWarnings && (
            <MessageBar intent="warning" style={{ marginTop: 16 }}>
              {directorySyncWarnings}
            </MessageBar>
          )}
        </div>
      )}
    </div>
  )
}
